import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { REASONING_EFFORT_OPTIONS, ReasoningEffort } from '@/shared/types/modelStrategy';

const INHERIT_SENTINEL = '__inherit__';

const LABEL_KEYS: Record<Exclude<ReasoningEffort, null>, string> = {
  low: 'ai_agents.model_strategy.reasoning_effort.low',
  medium: 'ai_agents.model_strategy.reasoning_effort.medium',
  high: 'ai_agents.model_strategy.reasoning_effort.high',
};

type Props = {
  value: ReasoningEffort;
  onChange: (next: ReasoningEffort) => void;
  disabled?: boolean;
  /** The selected model doesn't support reasoning effort — show a disabled "Not available" field. */
  unsupported?: boolean;
  /** Forwarded to SelectContent. Use to bump z-index above modals (e.g. `z-3000`). */
  contentClassName?: string;
  /** Forwarded to SelectTrigger — e.g. `bg-background` on a non-white surface. */
  triggerClassName?: string;
};

export const ReasoningEffortSelect = ({
  value,
  onChange,
  disabled,
  unsupported,
  contentClassName,
  triggerClassName,
}: Props) => {
  const { t } = useTranslation();
  const inheritLabel = t('ai_agents.model_strategy.reasoning_effort.inherit');

  // Models that don't support reasoning render a disabled "Not available" field,
  // so they're distinguishable from supported models explicitly set to "None".
  if (unsupported) {
    return (
      <Select disabled>
        <SelectTrigger className={triggerClassName}>
          <SelectValue placeholder={t('ai_agents.model_strategy.reasoning_effort.not_available')} />
        </SelectTrigger>
      </Select>
    );
  }

  return (
    <Select
      value={value ?? INHERIT_SENTINEL}
      onValueChange={(next) =>
        onChange(next === INHERIT_SENTINEL ? null : (next as ReasoningEffort))
      }
      disabled={disabled}
    >
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={inheritLabel} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        <SelectItem value={INHERIT_SENTINEL}>{inheritLabel}</SelectItem>
        {REASONING_EFFORT_OPTIONS.map((option) => (
          <SelectItem key={option} value={option}>
            {t(LABEL_KEYS[option])}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
